import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebase";
import { ref, push, onValue, remove, update, get, set } from "firebase/database";
import Barcode from "react-barcode";

export default function Books() {
  const navigate = useNavigate();
  const location = useLocation();
  const [books, setBooks] = useState({});
  const [name, setName] = useState("");
  const [author, setAuthor] = useState("");
  const [total, setTotal] = useState("");
  const [barcode, setBarcode] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [editingBook, setEditingBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedBookForBarcode, setSelectedBookForBarcode] = useState(null);
  const [printMode, setPrintMode] = useState("single");
  const [barcodeFormat, setBarcodeFormat] = useState("EAN13");
  const [printQuantity, setPrintQuantity] = useState(1);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const role = localStorage.getItem("role");
  const CACHE_KEY = "library_books_cache";
  const CACHE_TIMESTAMP_KEY = "library_books_timestamp";
  const CACHE_DURATION = 5 * 60 * 1000;

  const bottomNavItems = [
    { name: "Dashboard", path: "/admin-dashboard", icon: "🏠", activeIcon: "🏠" },
    { name: "Books", path: "/books", icon: "📚", activeIcon: "📚" },
    { name: "Teachers", path: "/teachers", icon: "👩‍🏫", activeIcon: "👩‍🏫" },
    { name: "Issue/Return", path: "/manage-books", icon: "🔄", activeIcon: "🔄" },
    { name: "Reports", path: "/reports", icon: "📊", activeIcon: "📊" },
    { name: "Settings", path: "/settings", icon: "⚙️", activeIcon: "⚙️" }
  ];

  // Generate EAN-13 barcode number
  const generateEAN13Barcode = (baseNumber = null) => {
    if (baseNumber) {
      let numStr = baseNumber.toString().replace(/\D/g, '');
      if (numStr.length > 12) numStr = numStr.slice(0, 12);
      if (numStr.length < 12) numStr = numStr.padStart(12, '0');
      
      let sum = 0;
      for (let i = 0; i < 12; i++) {
        const digit = parseInt(numStr[i]);
        sum += i % 2 === 0 ? digit : digit * 3;
      }
      const checksum = (10 - (sum % 10)) % 10;
      return numStr + checksum;
    }
    
    const prefix = "978";
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    let numStr = prefix + timestamp + random;
    numStr = numStr.slice(0, 12);
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(numStr[i]);
      sum += i % 2 === 0 ? digit : digit * 3;
    }
    const checksum = (10 - (sum % 10)) % 10;
    return numStr + checksum;
  };

  // Format barcode for display
  const formatBarcode = (barcodeNum) => {
    if (!barcodeNum) return "";
    const str = barcodeNum.toString();
    if (str.length === 13) {
      return `${str.slice(0, 3)} ${str.slice(3, 8)} ${str.slice(8, 13)}`;
    }
    return str;
  };

  // Generate barcode when adding new book
  const generateNewBarcode = () => {
    const newBarcode = generateEAN13Barcode();
    setBarcode(newBarcode);
  };

  // Load from localStorage cache first
  const loadFromCache = () => {
    const cachedBooks = localStorage.getItem(CACHE_KEY);
    const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    
    if (cachedBooks && cacheTimestamp) {
      const isExpired = Date.now() - parseInt(cacheTimestamp) > CACHE_DURATION;
      if (!isExpired) {
        setBooks(JSON.parse(cachedBooks));
        setLoading(false);
        return true;
      }
    }
    return false;
  };

  // Save to localStorage cache
  const saveToCache = (booksData) => {
    localStorage.setItem(CACHE_KEY, JSON.stringify(booksData));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  };

  // Fetch books from Firebase with caching
  useEffect(() => {
    const hasCache = loadFromCache();
    
    if (hasCache) {
      console.log("📦 Loaded books from cache");
    }

    const handleOnline = () => {
      setIsOffline(false);
      setError("");
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      setError("You are offline. Showing cached data.");
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const booksRef = ref(db, "books");
    const unsubscribe = onValue(booksRef, 
      (snapshot) => {
        const data = snapshot.val() || {};
        console.log("🔄 Synced with Firebase");
        setBooks(data);
        saveToCache(data);
        setLoading(false);
        setError("");
      },
      (error) => {
        console.error("❌ Firebase error:", error);
        if (!hasCache) {
          setError("Unable to load books. Using cache if available.");
        }
        setLoading(false);
      }
    );
    
    return () => {
      unsubscribe();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Save Book to Firebase
  const saveBook = async () => {
    if (!name || !name.trim()) {
      alert("❌ Please enter Book Name");
      return;
    }
    
    if (!total || total <= 0) {
      alert("❌ Please enter valid Total Quantity");
      return;
    }

    let finalBarcode = barcode.trim();
    if (!finalBarcode) {
      finalBarcode = generateEAN13Barcode();
      setBarcode(finalBarcode);
    }

    try {
      const bookData = {
        name: name.trim(),
        author: author.trim() || "Unknown Author",
        barcode: finalBarcode,
        total: Number(total),
        available: editingBook ? editingBook.available : Number(total),
        category: selectedCategory === "all" ? "General" : selectedCategory,
        createdAt: editingBook ? editingBook.createdAt : Date.now(),
        updatedAt: Date.now(),
        issuedCount: editingBook ? editingBook.issuedCount : 0
      };

      console.log("💾 Saving book to Firebase:", bookData);

      if (editingBook) {
        await update(ref(db, `books/${editingBook.id}`), bookData);
        const updatedBooks = { ...books };
        updatedBooks[editingBook.id] = bookData;
        saveToCache(updatedBooks);
        setBooks(updatedBooks);
        setSuccessMessage(`✏️ "${bookData.name}" Updated Successfully!`);
      } else {
        const existingBook = Object.values(books).find(book => book.barcode === finalBarcode);
        if (existingBook) {
          alert(`❌ A book with barcode "${finalBarcode}" already exists!\nBook: ${existingBook.name}`);
          return;
        }
        
        const newBookRef = push(ref(db, "books"));
        await set(newBookRef, bookData);
        const updatedBooks = { ...books, [newBookRef.key]: bookData };
        saveToCache(updatedBooks);
        setBooks(updatedBooks);
        setSuccessMessage(`✅ "${bookData.name}" Added Successfully!`);
      }
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      resetForm();
      
    } catch (error) {
      console.error("❌ Error saving book:", error);
      alert(`❌ Error saving book: ${error.message}`);
    }
  };

  // Reset form
  const resetForm = () => {
    setName("");
    setAuthor("");
    setBarcode("");
    setTotal("");
    setSelectedCategory("all");
    setEditingBook(null);
  };

  // Delete Book
  const deleteBook = async (id, bookName) => {
    if (window.confirm(`Are you sure you want to delete "${bookName}" permanently?`)) {
      try {
        await remove(ref(db, `books/${id}`));
        const updatedBooks = { ...books };
        delete updatedBooks[id];
        saveToCache(updatedBooks);
        setBooks(updatedBooks);
        setSuccessMessage(`🗑️ "${bookName}" Deleted Successfully!`);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
        console.error("❌ Error deleting book:", error);
        alert("❌ Error deleting book: " + error.message);
      }
    }
  };

  // Edit Book
  const editBook = (id, book) => {
    setEditingBook({ id, ...book });
    setName(book.name);
    setAuthor(book.author || "");
    setBarcode(book.barcode || "");
    setTotal(book.total.toString());
    setSelectedCategory(book.category === "General" ? "all" : book.category);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Print Single Barcode
  const printSingleBarcode = (book) => {
    setSelectedBookForBarcode(book);
    setPrintMode("single");
    setShowBarcodeModal(true);
  };

  // Print All Barcodes
  const printAllBarcodes = () => {
    setPrintMode("all");
    setShowBarcodeModal(true);
  };

  // Generate Barcode HTML for printing
  const generatePrintHTML = () => {
    if (printMode === "single" && selectedBookForBarcode) {
      const book = selectedBookForBarcode;
      let barcodesHTML = '';
      for (let i = 0; i < printQuantity; i++) {
        barcodesHTML += `
          <div class="barcode-item">
            <div class="barcode-container">
              <div class="book-title">${book.name}</div>
              <div class="barcode-number">${formatBarcode(book.barcode)}</div>
              <canvas id="barcode-canvas-${i}" class="barcode-canvas"></canvas>
              <div class="book-price">Price: ₹${Math.floor(Math.random() * 500 + 200)}</div>
            </div>
          </div>
        `;
      }
      
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print Barcode - ${book.name}</title>
          <style>
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
            }
            .barcode-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 20px;
            }
            .barcode-item {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .barcode-container {
              border: 1px solid #ddd;
              padding: 15px;
              border-radius: 8px;
              text-align: center;
              background: white;
            }
            .book-title {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
            }
            .barcode-number {
              font-size: 12px;
              font-family: monospace;
              margin: 10px 0;
              letter-spacing: 1px;
            }
            .book-price {
              font-size: 12px;
              color: #666;
              margin-top: 10px;
            }
            .barcode-canvas {
              width: 100%;
              max-width: 300px;
              margin: 10px auto;
              display: block;
            }
            @media print {
              body {
                padding: 0;
              }
              .no-print {
                display: none;
              }
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        </head>
        <body>
          <div class="no-print" style="text-align:center; margin-bottom:20px;">
            <button onclick="window.print()" style="padding:10px 20px; margin:10px; cursor:pointer;">🖨️ Print</button>
            <button onclick="window.close()" style="padding:10px 20px; margin:10px; cursor:pointer;">Close</button>
          </div>
          <div class="barcode-grid">
            ${barcodesHTML}
          </div>
          <script>
            window.onload = function() {
              const barcodeValue = "${book.barcode}";
              for(let i = 0; i < ${printQuantity}; i++) {
                const canvas = document.getElementById('barcode-canvas-' + i);
                JsBarcode(canvas, barcodeValue, {
                  format: "${barcodeFormat}",
                  width: 2,
                  height: 50,
                  displayValue: true,
                  fontSize: 14,
                  margin: 10
                });
              }
            }
          </script>
        </body>
        </html>
      `;
    } else if (printMode === "all") {
      const booksList = Object.values(books);
      let barcodesHTML = '';
      booksList.forEach((book, idx) => {
        for (let i = 0; i < 2; i++) {
          barcodesHTML += `
            <div class="barcode-item">
              <div class="barcode-container">
                <div class="book-title">${book.name}</div>
                <div class="barcode-number">${formatBarcode(book.barcode)}</div>
                <canvas id="barcode-canvas-${idx}-${i}" class="barcode-canvas"></canvas>
                <div class="book-price">Price: ₹${Math.floor(Math.random() * 500 + 200)}</div>
              </div>
            </div>
          `;
        }
      });
      
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Print All Book Barcodes</title>
          <style>
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
            }
            .barcode-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 20px;
            }
            .barcode-item {
              break-inside: avoid;
              page-break-inside: avoid;
            }
            .barcode-container {
              border: 1px solid #ddd;
              padding: 15px;
              border-radius: 8px;
              text-align: center;
              background: white;
            }
            .book-title {
              font-size: 14px;
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
            }
            .barcode-number {
              font-size: 12px;
              font-family: monospace;
              margin: 10px 0;
              letter-spacing: 1px;
            }
            .book-price {
              font-size: 12px;
              color: #666;
              margin-top: 10px;
            }
            .barcode-canvas {
              width: 100%;
              max-width: 300px;
              margin: 10px auto;
              display: block;
            }
            @media print {
              body {
                padding: 0;
              }
              .no-print {
                display: none;
              }
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        </head>
        <body>
          <div class="no-print" style="text-align:center; margin-bottom:20px;">
            <button onclick="window.print()" style="padding:10px 20px; margin:10px; cursor:pointer;">🖨️ Print All</button>
            <button onclick="window.close()" style="padding:10px 20px; margin:10px; cursor:pointer;">Close</button>
          </div>
          <div class="barcode-grid">
            ${barcodesHTML}
          </div>
          <script>
            window.onload = function() {
              const books = ${JSON.stringify(booksList)};
              books.forEach((book, idx) => {
                for(let i = 0; i < 2; i++) {
                  const canvas = document.getElementById('barcode-canvas-' + idx + '-' + i);
                  if(canvas) {
                    JsBarcode(canvas, book.barcode, {
                      format: "${barcodeFormat}",
                      width: 2,
                      height: 50,
                      displayValue: true,
                      fontSize: 14,
                      margin: 10
                    });
                  }
                }
              });
            }
          </script>
        </body>
        </html>
      `;
    }
  };

  // Handle print
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatePrintHTML());
    printWindow.document.close();
    setShowBarcodeModal(false);
  };

  // Filter books
  const filteredBooks = Object.entries(books).filter(([id, book]) => {
    const matchesSearch = book.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         book.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (book.barcode && book.barcode.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === "all" || book.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Statistics
  const totalBooks = filteredBooks.length;
  const totalCopies = filteredBooks.reduce((sum, [id, book]) => sum + (book.total || 0), 0);
  const availableCopies = filteredBooks.reduce((sum, [id, book]) => sum + (book.available || 0), 0);

  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
  };

  // Handle logout
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("user");
      localStorage.removeItem("adminLoggedIn");
      localStorage.removeItem("role");
      navigate("/");
    }
  };

  // Access control
  if (role !== "admin") {
    return (
      <div style={styles.accessDenied}>
        <div style={styles.accessDeniedCard}>
          <span style={styles.accessDeniedIcon}>🔒</span>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <button onClick={() => navigate("/admin-dashboard")} style={styles.backBtn}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {isOffline && (
        <div style={styles.offlineBanner}>
          ⚠️ You are offline. Showing cached data. Changes will sync when online.
        </div>
      )}

      {showSuccess && (
        <div style={styles.toast}>
          {successMessage}
        </div>
      )}

      {error && (
        <div style={styles.errorToast}>
          ⚠️ {error}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📚 Book Management</h1>
          <p style={styles.subtitle}>Manage your library collection with barcode generation & printing</p>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.adminProfile} onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <div style={styles.adminAvatar}>
              {localStorage.getItem("userName")?.charAt(0) || "A"}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {showMobileMenu && (
        <div style={styles.mobileMenu}>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/profile"); setShowMobileMenu(false); }}>
            <span>👤</span> My Profile
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { navigate("/settings"); setShowMobileMenu(false); }}>
            <span>⚙️</span> Settings
          </div>
          <div style={styles.mobileMenuItem} onClick={() => { handleLogout(); setShowMobileMenu(false); }}>
            <span>🚪</span> Logout
          </div>
        </div>
      )}

      {/* Barcode Print Modal */}
      {showBarcodeModal && (
        <div style={styles.modal}>
          <div style={styles.modalContentLarge}>
            <div style={styles.modalHeader}>
              <h3>🏷️ Print Barcode Labels</h3>
              <button onClick={() => setShowBarcodeModal(false)} style={styles.closeModalBtn}>✕</button>
            </div>
            <div style={styles.printOptions}>
              {printMode === "single" && (
                <>
                  <div style={styles.printOptionGroup}>
                    <label>Print Quantity:</label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={printQuantity}
                      onChange={(e) => setPrintQuantity(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                      style={styles.printInput}
                    />
                  </div>
                  <div style={styles.printOptionGroup}>
                    <label>Barcode Format:</label>
                    <select
                      value={barcodeFormat}
                      onChange={(e) => setBarcodeFormat(e.target.value)}
                      style={styles.printSelect}
                    >
                      <option value="EAN13">EAN-13 (13 digits)</option>
                      <option value="UPC">UPC (12 digits)</option>
                      <option value="CODE128">Code 128</option>
                      <option value="CODE39">Code 39</option>
                    </select>
                  </div>
                </>
              )}
              <div style={styles.printButtons}>
                <button onClick={handlePrint} style={styles.printBtn}>
                  🖨️ Generate & Print
                </button>
                <button onClick={() => setShowBarcodeModal(false)} style={styles.cancelPrintBtn}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>📖</span>
          <div>
            <h3 style={styles.statValue}>{totalBooks}</h3>
            <p style={styles.statLabel}>Total Books</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>📚</span>
          <div>
            <h3 style={styles.statValue}>{totalCopies}</h3>
            <p style={styles.statLabel}>Total Copies</p>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statIcon}>✅</span>
          <div>
            <h3 style={styles.statValue}>{availableCopies}</h3>
            <p style={styles.statLabel}>Available</p>
          </div>
        </div>
        <div style={styles.statCard} onClick={printAllBarcodes}>
          <span style={styles.statIcon}>🏷️</span>
          <div>
            <h3 style={styles.statValue}>Print All</h3>
            <p style={styles.statLabel}>Generate Barcodes</p>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      <div style={styles.formCard}>
        <h2 style={styles.formTitle}>
          {editingBook ? "✏️ Edit Book" : "➕ Add New Book"}
        </h2>
        
        <div style={styles.formGroup}>
          <label style={styles.label}>Book Name *</label>
          <input
            placeholder="Enter book name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Author</label>
          <input
            placeholder="Enter author name"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Total Quantity *</label>
          <input
            placeholder="Number of copies"
            type="number"
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Barcode (EAN-13 Format)</label>
          <div style={styles.barcodeSection}>
            <input
              placeholder="Enter 13-digit barcode or auto-generate"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              style={{...styles.input, flex: 1, marginRight: "10px"}}
            />
            <button onClick={generateNewBarcode} style={styles.generateBtn} type="button">
              🔄 Generate Barcode
            </button>
          </div>
          <p style={styles.hint}>💡 Tip: Leave empty to auto-generate EAN-13 barcode (format: 978XXXXXXXXX)</p>
          {barcode && (
            <div style={styles.barcodePreview}>
              <p style={styles.previewLabel}>Barcode Preview:</p>
              <div style={styles.barcodeDisplay}>
                <Barcode 
                  value={barcode} 
                  format="EAN13"
                  width={2}
                  height={50}
                  displayValue={true}
                  fontSize={14}
                  margin={10}
                />
              </div>
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Category</label>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={styles.select}
          >
            <option value="all">Select Category</option>
            <option value="Fiction">📖 Fiction</option>
            <option value="Non-Fiction">📕 Non-Fiction</option>
            <option value="Science">🔬 Science</option>
            <option value="Technology">💻 Technology</option>
            <option value="History">🏛️ History</option>
            <option value="Children">🧸 Children</option>
            <option value="Academic">🎓 Academic</option>
            <option value="Magazine">📰 Magazine</option>
          </select>
        </div>

        <div style={styles.formButtons}>
          <button onClick={saveBook} style={styles.addBtn}>
            {editingBook ? "✏️ Update Book" : "➕ Add Book to Library"}
          </button>
          {editingBook && (
            <button onClick={resetForm} style={styles.cancelBtn}>
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Search and Filter */}
      <div style={styles.searchSection}>
        <div style={styles.searchBox}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            placeholder="Search by name, author or barcode..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <div style={styles.filterBox}>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Categories</option>
            <option value="Fiction">Fiction</option>
            <option value="Non-Fiction">Non-Fiction</option>
            <option value="Science">Science</option>
            <option value="Technology">Technology</option>
            <option value="History">History</option>
            <option value="Children">Children</option>
            <option value="Academic">Academic</option>
          </select>
        </div>
      </div>

      {/* Books Grid */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Loading books...</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredBooks.length > 0 ? (
            filteredBooks.map(([id, book]) => (
              <div key={id} style={styles.bookCard}>
                <div style={styles.bookHeader}>
                  <span style={styles.bookCategory}>{book.category || "📖 General"}</span>
                  <div style={styles.bookActions}>
                    <button 
                      onClick={() => printSingleBarcode(book)} 
                      style={styles.printIconBtn}
                      title="Print Barcode"
                    >
                      🏷️
                    </button>
                    <button 
                      onClick={() => deleteBook(id, book.name)} 
                      style={styles.deleteBtn}
                      title="Delete Book"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <h3 style={styles.bookTitle}>{book.name}</h3>
                <p style={styles.bookAuthor}>by {book.author}</p>
                <div style={styles.bookDetails}>
                  <div style={styles.detailRow}>
                    <span>🔖 Barcode:</span>
                    <strong style={{fontFamily: 'monospace'}}>{formatBarcode(book.barcode)}</strong>
                  </div>
                  <div style={styles.detailRow}>
                    <span>📦 Stock:</span>
                    <strong style={{color: book.available > 0 ? "#48bb78" : "#e53e3e"}}>
                      {book.available || 0}/{book.total}
                    </strong>
                  </div>
                </div>
                <div style={styles.progressContainer}>
                  <div style={styles.progressBar}>
                    <div style={{
                      ...styles.progressFill,
                      width: `${((book.available || 0) / book.total) * 100}%`
                    }}></div>
                  </div>
                  <span style={styles.progressText}>
                    {Math.round(((book.available || 0) / book.total) * 100)}% Available
                  </span>
                </div>
                <button onClick={() => editBook(id, book)} style={styles.editBtn}>
                  ✏️ Edit Book
                </button>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>📚</span>
              <h3>No Books Found</h3>
              <p>Add your first book by filling the form above</p>
              <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={styles.addFirstBtn}>
                + Add New Book
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <div style={styles.bottomNav}>
        {bottomNavItems.map((item) => (
          <div
            key={item.path}
            style={{
              ...styles.bottomNavItem,
              ...(location.pathname === item.path && styles.bottomNavItemActive)
            }}
            onClick={() => handleNavigation(item.path)}
          >
            <div style={styles.bottomNavIcon}>
              {location.pathname === item.path ? item.activeIcon : item.icon}
            </div>
            <span style={styles.bottomNavLabel}>{item.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "20px",
    paddingBottom: "80px",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },

  offlineBanner: {
    background: "#ed8936",
    color: "#fff",
    padding: "12px",
    textAlign: "center",
    borderRadius: "8px",
    marginBottom: "20px",
    fontWeight: "500",
  },

  toast: {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#48bb78",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: "8px",
    zIndex: 1000,
    animation: "slideIn 0.3s ease",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },

  errorToast: {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#e53e3e",
    color: "#fff",
    padding: "12px 20px",
    borderRadius: "8px",
    zIndex: 1000,
    animation: "slideIn 0.3s ease",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },

  title: {
    fontSize: "clamp(24px, 5vw, 32px)",
    fontWeight: "700",
    color: "#fff",
    margin: "0 0 8px 0",
  },

  subtitle: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.9)",
    margin: 0,
  },

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },

  adminProfile: {
    cursor: "pointer",
  },

  adminAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "18px",
  },

  mobileMenu: {
    position: "absolute",
    top: "70px",
    right: "20px",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    zIndex: 1000,
    minWidth: "160px",
    overflow: "hidden",
  },

  mobileMenuItem: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "14px",
    color: "#2d3748",
    cursor: "pointer",
    transition: "background 0.2s ease",
    borderBottom: "1px solid #e2e8f0",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: "16px",
    marginBottom: "24px",
  },

  statCard: {
    background: "#fff",
    padding: "16px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    cursor: "pointer",
    transition: "transform 0.2s ease",
  },

  statIcon: {
    fontSize: "32px",
  },

  statValue: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
    color: "#2d3748",
  },

  statLabel: {
    fontSize: "12px",
    color: "#718096",
    margin: 0,
  },

  formCard: {
    background: "#fff",
    padding: "24px",
    borderRadius: "16px",
    marginBottom: "24px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  },

  formTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "20px",
    color: "#2d3748",
  },

  formGroup: {
    marginBottom: "16px",
  },

  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "8px",
    color: "#4a5568",
  },

  input: {
    width: "100%",
    padding: "12px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    transition: "all 0.2s ease",
  },

  barcodeSection: {
    display: "flex",
    gap: "10px",
  },

  generateBtn: {
    padding: "12px 20px",
    background: "#ed8936",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    whiteSpace: "nowrap",
  },

  barcodePreview: {
    marginTop: "12px",
    padding: "12px",
    background: "#f7fafc",
    borderRadius: "8px",
    textAlign: "center",
  },

  previewLabel: {
    fontSize: "12px",
    color: "#718096",
    marginBottom: "8px",
  },

  barcodeDisplay: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px",
    background: "#fff",
    borderRadius: "8px",
  },

  hint: {
    fontSize: "11px",
    color: "#a0aec0",
    marginTop: "6px",
  },

  select: {
    width: "100%",
    padding: "12px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
    backgroundColor: "#fff",
  },

  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.9)",
    zIndex: 2000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  modalContentLarge: {
    background: "#fff",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "600px",
    padding: "20px",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },

  closeModalBtn: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    padding: "0 8px",
  },

  printOptions: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },

  printOptionGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  printInput: {
    padding: "10px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
  },

  printSelect: {
    padding: "10px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "14px",
  },

  printButtons: {
    display: "flex",
    gap: "12px",
    marginTop: "10px",
  },

  printBtn: {
    flex: 1,
    padding: "12px",
    background: "#48bb78",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
  },

  cancelPrintBtn: {
    flex: 1,
    padding: "12px",
    background: "#a0aec0",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
  },

  formButtons: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
  },

  addBtn: {
    flex: 1,
    padding: "12px",
    background: "#48bb78",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },

  cancelBtn: {
    flex: 1,
    padding: "12px",
    background: "#a0aec0",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
  },

  searchSection: {
    display: "flex",
    gap: "12px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },

  searchBox: {
    flex: 2,
    display: "flex",
    alignItems: "center",
    background: "#fff",
    borderRadius: "12px",
    padding: "4px 12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },

  searchIcon: {
    fontSize: "18px",
    marginRight: "8px",
  },

  searchInput: {
    flex: 1,
    padding: "12px",
    border: "none",
    outline: "none",
    fontSize: "14px",
  },

  filterBox: {
    flex: 1,
  },

  filterSelect: {
    width: "100%",
    padding: "12px",
    border: "none",
    borderRadius: "12px",
    fontSize: "14px",
    backgroundColor: "#fff",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },

  loadingContainer: {
    textAlign: "center",
    padding: "60px",
    background: "#fff",
    borderRadius: "16px",
  },

  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #e2e8f0",
    borderTopColor: "#667eea",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 16px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "20px",
  },

  bookCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    transition: "transform 0.2s ease",
  },

  bookHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },

  bookCategory: {
    fontSize: "11px",
    padding: "4px 8px",
    background: "#edf2f7",
    borderRadius: "6px",
    color: "#4a5568",
  },

  bookActions: {
    display: "flex",
    gap: "8px",
  },

  printIconBtn: {
    background: "none",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "6px",
    transition: "all 0.2s ease",
  },

  deleteBtn: {
    background: "none",
    border: "none",
    fontSize: "18px",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "6px",
    transition: "all 0.2s ease",
  },

  bookTitle: {
    fontSize: "18px",
    fontWeight: "600",
    margin: "0 0 8px 0",
    color: "#2d3748",
  },

  bookAuthor: {
    fontSize: "14px",
    color: "#718096",
    margin: "0 0 16px 0",
  },

  bookDetails: {
    marginBottom: "16px",
  },

  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    marginBottom: "8px",
    color: "#4a5568",
  },

  progressContainer: {
    marginBottom: "16px",
  },

  progressBar: {
    height: "6px",
    background: "#e2e8f0",
    borderRadius: "3px",
    overflow: "hidden",
    marginBottom: "8px",
  },

  progressFill: {
    height: "100%",
    background: "#48bb78",
    borderRadius: "3px",
    transition: "width 0.3s ease",
  },

  progressText: {
    fontSize: "11px",
    color: "#718096",
  },

  editBtn: {
    width: "100%",
    padding: "10px",
    background: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  },

  emptyState: {
    gridColumn: "1 / -1",
    textAlign: "center",
    padding: "60px",
    background: "#fff",
    borderRadius: "16px",
  },

  emptyIcon: {
    fontSize: "64px",
    display: "block",
    marginBottom: "16px",
  },

  addFirstBtn: {
    marginTop: "16px",
    padding: "12px 24px",
    background: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },

  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "8px 16px",
    boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
    zIndex: 100,
    borderTop: "1px solid #e2e8f0",
  },

  bottomNavItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    flex: 1,
  },

  bottomNavItemActive: {
    color: "#667eea",
  },

  bottomNavIcon: {
    fontSize: "22px",
  },

  bottomNavLabel: {
    fontSize: "11px",
    fontWeight: "500",
  },

  accessDenied: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },

  accessDeniedCard: {
    textAlign: "center",
    padding: "40px",
    background: "#fff",
    borderRadius: "16px",
    maxWidth: "400px",
    margin: "20px",
  },

  accessDeniedIcon: {
    fontSize: "64px",
    display: "block",
    marginBottom: "16px",
  },

  backBtn: {
    marginTop: "20px",
    padding: "12px 24px",
    background: "#667eea",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer",
  },
};

// Add animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  input:focus, select:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  button:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  button:active {
    transform: translateY(0);
  }
  
  .book-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.15);
  }
  
  .stat-card:hover {
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    .container {
      padding: 16px;
      padding-bottom: 70px;
    }
    
    .form-card {
      padding: 16px;
    }
    
    .barcode-section {
      flex-direction: column;
    }
    
    .generate-btn {
      width: 100%;
    }
    
    .search-section {
      flex-direction: column;
    }
    
    .grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }
    
    .stats-grid {
      grid-template-columns: 1fr;
    }
    
    .modal-content, .modal-content-large {
      width: 95%;
      margin: 10px;
    }
  }
  
  @media (min-width: 769px) and (max-width: 1024px) {
    .grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
`;
document.head.appendChild(styleSheet);